import React, { useEffect } from 'react'
import { Paper, useTheme } from '@mui/material_modern'
import { styled } from '@mui/system'
import { DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro_modern'

interface StyledPaperProps {
  width?: string
  height?: string
  styleProps?: Record<string, any>
  children?: React.ReactNode
}

// Helper function to determine which props should not be forwarded to the DOM
const shouldForwardProp = (prop: string): boolean => prop !== 'styleProps' && prop !== 'width' && prop !== 'height'

const StyledPaper = styled(Paper, {
  shouldForwardProp: shouldForwardProp,
})<StyledPaperProps>(({ theme, width, height, styleProps }) => ({
  // Default styles for width and height
  width: width || '100%',
  height: height || '100%',
  backgroundColor: theme.palette.background.default,
  // Specific styles for DataGrid cells
  '.MuiDataGrid-cell': {
    paddingLeft: `${theme.spacing(2)} !important`,
  },
  // Apply additional custom styles if provided
  ...styleProps,
}))

interface BasicDataGridV2Props extends DataGridProProps {
  width?: string
  height?: string
  loading?: boolean
  onMounted?: () => void
  styleProps?: Record<string, any>
  dataTestId?: string
  columns: Array<any>
  [key: string]: any
}

const BasicDataGridV2: React.FC<BasicDataGridV2Props> = ({
  columns = [],
  rows = [],
  width = '100%',
  height = '100%',
  loading = false,
  onMounted = () => {},
  styleProps = {},
  ...restProps
}) => {
  useEffect(() => {
    let newTimeout
    if (!loading) {
      newTimeout = setTimeout(onMounted, 500)
    }
    return () => {
      clearTimeout(newTimeout)
    }
  }, [loading])

  const theme = useTheme()

  const handleSortChange = newSortModel => {
    // update the "filter" object with the new sort field and direction
    if (newSortModel.length > 0 && restProps.setFilter) {
      const { field, sort } = newSortModel[0]
      restProps.setFilter(prevFilter => ({
        ...prevFilter,
        sort: {
          field: field,
          ascending: sort === 'asc',
        },
      }))
    }
  }

  const { dataTestId = 'datagrid-root' } = restProps
  return (
    <StyledPaper
      elevation={0}
      theme={theme}
      data-testid={dataTestId}
      width={width}
      height={height}
      styleProps={styleProps}
    >
      <DataGridPro
        loading={loading}
        autoPageSize
        autoHeight
        rows={rows}
        columns={columns}
        onSortModelChange={handleSortChange}
        {...restProps}
      />
    </StyledPaper>
  )
}

export default BasicDataGridV2
